import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { FiMail, FiMapPin } from "react-icons/fi";
import { api } from "../services/api";
import { getIcon } from "../utils/iconMapper";
import logoImg from "../assets/images/logo.png";

const Footer = () => {
  const [organization, setOrganization] = useState(null);
  const [quickLinks, setQuickLinks] = useState([]);
  const [socialLinks, setSocialLinks] = useState([]);
  const [offices, setOffices] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [orgData, linksData, socialData, officesData] = await Promise.all([
          api.getOrganization(),
          api.getQuickLinks(),
          api.getSocialLinks(),
          api.getOffices(),
        ]);
        setOrganization(orgData);
        setQuickLinks(linksData);
        setSocialLinks(socialData);
        setOffices(officesData);
      } catch (error) {
        console.error("Error fetching footer data:", error);
      }
    };
    fetchData();
  }, []);

  const currentYear = new Date().getFullYear();
  const firstOffice = offices[0];

  return (
    <footer className="bg-gray-900 text-white">
      <div className="container-custom">
        <div className="py-16 grid grid-cols-1 md:grid-cols-3 gap-8">
          {/* Organization Info */}
          <div className="lg:col-span-1">
            <Link to="/" className="flex items-center gap-3 mb-6">
              <img src={logoImg} alt="Prasoon Logo" className="h-12 w-12 rounded-full object-cover" />
              <span className="text-2xl font-bold font-poppins">{organization?.name || "PRASOON"}</span>
            </Link>
            <p className="text-gray-300 mb-6 leading-relaxed">
              {organization?.tagline ? `${organization.tagline}. ` : ""}
              Empowering women, education, health, livelihoods, and cultural preservation in rural India through sustainable development initiatives.
            </p>
            <div className="space-y-3">
              {firstOffice && (
                <div className="flex items-start gap-3 text-gray-300">
                  <FiMapPin className="text-primary-400 flex-shrink-0 mt-1" />
                  <span>{firstOffice.address.city}, {firstOffice.address.state}</span>
                </div>
              )}
              <div className="flex items-center gap-3 text-gray-300">
                <FiMail className="text-primary-400" />
                <a href={`mailto:${organization?.email}`} className="hover:text-primary-400">
                  {organization?.email || "prasoon.org@gmail.com"}
                </a>
              </div>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-semibold mb-6 font-poppins">Quick Links</h3>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.id}>
                  <Link
                    to={link.href}
                    className="text-gray-300 hover:text-primary-400 transition-colors duration-200"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Social Links */}
          <div>
            <h3 className="text-lg font-semibold mb-6 font-poppins">Follow Us</h3>
            <p className="text-gray-300 mb-6">
              Connect with us on social media for updates on our work and impact stories.
            </p>
            <div className="flex gap-4">
              {socialLinks.map((social) => {
                const Icon = getIcon(social.icon);
                return (
                  <a
                    key={social.id}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center text-gray-300 hover:text-primary-400 hover:bg-gray-700 transition-all duration-200"
                    aria-label={social.label}
                  >
                    <Icon size={20} />
                  </a>
                );
              })}
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-800 py-6">
          <div className="flex justify-center items-center">
            <p className="text-gray-400 text-sm">
              © {currentYear} {organization?.name || "PRASOON"} NGO. All rights reserved.
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
